/**
 * Packet → ONE merged PDF — the Create/Edit Packet modal's "Merge all forms submitted within this
 * packet into a single PDF" setting.
 *
 * WHY THIS FILE EXISTS
 *   Every form in a packet files its OWN PDF into Open Dental imaging under its OWN category ("Save
 *   PDF to Open Dental Imaging module under Category" in the form builder). Some offices want a
 *   packet (the New Patient Packet) filed as one combined PDF under one category picked on the
 *   packet, while the same form sent on its own keeps its own category. forms-service does the
 *   merging (lib/packetPdfBatch.js); this file is the modal's half of the contract:
 *     - what the modal must validate before Save is enabled,
 *     - the exact payload keys /save-packet expects,
 *     - the default file name prefilled from the packet name.
 *
 * THE PAYLOAD CONTRACT (forms-service savePacket)
 *   mergePdf          boolean, ALWAYS sent from this screen. forms-service leaves the three columns
 *                     untouched when the key is ABSENT (so older clients can't switch it off on a
 *                     rename) — which means this modal must send `false` explicitly, or un-ticking the
 *                     box would silently do nothing. Same rule as isNewPatientPacket.
 *   mergePdfCategory  OD image-category DefNum (number) when ON, null when OFF
 *   mergePdfFileName  trimmed string (≤120) when ON, null when OFF
 *   ON without a category or a file name is a 400 server-side; the modal disables Save first.
 *
 * Import-free on purpose: cypress/node-tests/packet-merge-pdf.test.js loads and executes THIS file
 * (strip the trailing `export {...}` + `new Function`), so the tests verify production code rather
 * than a copy. Do not add an `import` or a `require` here.
 */

/** Same cap as the form builder's "Select file name for this form" and the DB column. */
var MERGE_PDF_FILE_NAME_MAX = 120;

/** Same cap as teamio-admin/form-imaging-defaults.js slugifyFormName. */
var MERGE_PDF_SLUG_MAX = 40;

/**
 * Default merged-PDF file name from the packet name: lowercase, every run of non-alphanumerics → one
 * hyphen, trimmed, capped at 40 characters on a whole word. Mirrors teamio-admin's
 * slugifyFormName so packet PDFs read like the seeded form PDFs. Never returns '' — falls back to
 * 'packet'.
 *   "New Patient Packet"  → "new-patient-packet"
 * @param {*} name
 * @returns {string}
 */
function packetFileNameSlug(name) {
    var slug = String(name === undefined || name === null ? '' : name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (slug.length > MERGE_PDF_SLUG_MAX) {
        var cut = slug.slice(0, MERGE_PDF_SLUG_MAX);
        if (slug[MERGE_PDF_SLUG_MAX] !== '-') {
            var i = cut.lastIndexOf('-');
            if (i > 0) cut = cut.slice(0, i);
        }
        slug = cut.replace(/-+$/g, '');
    }
    return slug || 'packet';
}

/** A <select> value is a DefNum only when it is a strict positive integer ('' = --Please Select--). */
function toCategoryDefNum(value) {
    if (typeof value === 'number') return Number.isInteger(value) && value > 0 ? value : null;
    if (typeof value !== 'string') return null;
    var s = value.trim();
    if (!/^\d+$/.test(s)) return null;
    var n = parseInt(s, 10);
    return n > 0 ? n : null;
}

/**
 * Is the merge setting complete enough to save?
 * @param {{mergePdf: boolean, category: *, fileName: *}} state
 * @returns {{ok: boolean, categoryMissing: boolean, fileNameMissing: boolean, fileNameTooLong: boolean}}
 *          OFF is always ok — the category and name only matter when the box is ticked.
 */
function validatePacketMerge(state) {
    var s = state || {};
    if (s.mergePdf !== true) {
        return { ok: true, categoryMissing: false, fileNameMissing: false, fileNameTooLong: false };
    }
    var name = typeof s.fileName === 'string' ? s.fileName.trim() : '';
    var categoryMissing = toCategoryDefNum(s.category) === null;
    var fileNameMissing = name.length === 0;
    var fileNameTooLong = name.length > MERGE_PDF_FILE_NAME_MAX;
    return {
        ok: !categoryMissing && !fileNameMissing && !fileNameTooLong,
        categoryMissing: categoryMissing,
        fileNameMissing: fileNameMissing,
        fileNameTooLong: fileNameTooLong
    };
}

/**
 * The three /save-packet keys. `mergePdf` is ALWAYS present (see the header).
 * @param {{mergePdf: boolean, category: *, fileName: *}} state
 * @returns {{mergePdf: boolean, mergePdfCategory: (number|null), mergePdfFileName: (string|null)}}
 */
function buildPacketMergeFields(state) {
    var s = state || {};
    var on = s.mergePdf === true;
    return {
        mergePdf: on,
        mergePdfCategory: on ? toCategoryDefNum(s.category) : null,
        mergePdfFileName: on ? (typeof s.fileName === 'string' ? s.fileName.trim() : '') : null
    };
}

/**
 * Read a packet row (getPackets / getPacket) back into modal state. A packet saved before this
 * setting existed has no MergePdf, which reads as OFF — correct.
 * @param {Object} packet
 * @returns {{mergePdf: boolean, category: (number|string), fileName: string}}
 */
function readPacketMergeState(packet) {
    var p = packet || {};
    var category = toCategoryDefNum(p.MergePdfCategory);
    return {
        mergePdf: Number(p.MergePdf) === 1,
        category: category === null ? '' : category,
        fileName: typeof p.MergePdfFileName === 'string' ? p.MergePdfFileName : ''
    };
}

export { MERGE_PDF_FILE_NAME_MAX, packetFileNameSlug, validatePacketMerge, buildPacketMergeFields, readPacketMergeState };
